import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Instagram, Facebook, Linkedin } from 'lucide-react';

const Footer = () => {
    const socialLinks = [
        { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/communitypluscare/', color: 'hover:bg-gradient-to-r hover:from-purple-500 hover:via-pink-500 hover:to-orange-400' },
        { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/share/1HDoc5QuZB/', color: 'hover:bg-[#1877F2]' },
        { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/groups/17433006', color: 'hover:bg-[#0A66C2]' },
    ];

    return (
        <footer className="bg-[#F5F5F5] text-gray-700 pt-12 pb-6 scroll-mt-32" id="contact">
            <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                    {/* Column 1: Branding */}
                    <div className="flex flex-col items-start px-4">
                        <img
                            src="/assets/Community Plus Care (1).svg"
                            alt="Community Plus Care Services"
                            className="h-20 w-auto mb-6"
                        />
                        <h4 className="font-bold text-gray-800 text-lg uppercase tracking-wide mb-2">Community Plus Care Services</h4>
                        <p className="text-gray-600 text-sm leading-relaxed max-w-xs">
                            Empowering Independence, Choice & Community Inclusion.
                        </p>
                    </div>

                    {/* Column 2: Contact Us */}
                    <div className="px-4">
                        <h3 className="text-[#D99C3B] font-serif text-2xl font-bold mb-6">Contact Us</h3>
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-bold text-[#008080] mb-1">South Australia</h4>
                                <p className="text-xs text-gray-500 mb-2">Serving participants across South Australia</p>
                                <div className="flex items-center text-sm">
                                    <Phone size={16} className="mr-3 text-[#008080]" />
                                    <a href="tel:0489235263" className="hover:text-[#008080] transition-colors">0489 235 263</a>
                                </div>
                            </div>

                            <div>
                                <h4 className="font-bold text-[#008080] mb-1 mt-4">Queensland</h4>
                                <p className="text-xs text-gray-500 mb-2">Serving participants across Queensland</p>
                                <div className="flex items-center text-sm">
                                    <Phone size={16} className="mr-3 text-[#008080]" />
                                    <a href="tel:0412819650" className="hover:text-[#008080] transition-colors">0412 819 650</a>
                                </div>
                            </div>

                            <div className="mt-4 pt-2 border-t border-gray-200 text-sm">
                                <div className="flex items-center mb-1">
                                    <Mail size={16} className="mr-3 text-[#008080] flex-shrink-0" />
                                    <p>Admin@communitypluscare.com.au</p>
                                </div>
                                <p className="text-gray-500 pl-7">Appointments available by arrangement.</p>
                            </div>
                        </div>
                    </div>

                    {/* Column 3: Quick Links */}
                    <div className="px-4">
                        <h3 className="text-[#D99C3B] font-serif text-2xl font-bold mb-6">Quick Links</h3>
                        <div className="flex flex-col space-y-2 text-sm font-medium text-gray-600 mb-8">
                            <Link to="/" className="hover:text-[#008080] transition-colors w-fit">Home</Link>
                            <Link to="/about" className="hover:text-[#008080] transition-colors w-fit">About</Link>
                            <Link to="/services" className="hover:text-[#008080] transition-colors w-fit">Services</Link>
                            <Link to="/careers" className="hover:text-[#008080] transition-colors w-fit">Careers</Link>
                            <Link to="/register" className="hover:text-[#008080] transition-colors w-fit">Register</Link>
                            <Link to="/contact" className="hover:text-[#008080] transition-colors w-fit">Contact Us</Link>
                        </div>

                        {/* NDIS Registered Provider Badge - Prominent */}
                        <div className="mt-6 bg-white border-2 border-[#008080] rounded-lg p-4 flex items-center gap-4 shadow-sm">
                            <img
                                src="/assets/We Heart NDIS_2020.svg"
                                alt="We Support NDIS"
                                className="h-14 w-auto"
                            />
                            <div>
                                <p className="text-[#008080] font-bold text-lg">Registered NDIS Provider</p>
                            </div>
                        </div>
                    </div>

                    {/* Column 4: Connect With Us */}
                    <div className="px-4">
                        <h3 className="text-[#D99C3B] font-serif text-2xl font-bold mb-6">Connect With Us</h3>
                        <p className="text-gray-600 text-sm mb-6">Follow us on social media to stay updated with our latest news and events.</p>
                        <div className="flex gap-4">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.name}
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`w-12 h-12 rounded-full bg-[#008080] flex items-center justify-center text-white transition-all duration-300 transform hover:scale-110 hover:shadow-lg ${social.color}`}
                                    aria-label={social.name}
                                >
                                    <social.icon size={22} />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Aboriginal and Torres Strait Islander Acknowledgement */}
                <div className="border-t border-gray-300 pt-6 pb-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <div className="w-8 h-0.5 bg-[#D99C3B]"></div>
                            <span className="text-[#D99C3B] text-sm font-semibold uppercase tracking-wider">Acknowledgement of Country</span>
                            <div className="w-8 h-0.5 bg-[#D99C3B]"></div>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Community Plus Care Services acknowledges the Traditional Owners of the land and recognises their continuing connection to culture, community, and Country. We pay our respects to Elders past, present, and emerging.
                        </p>
                    </div>
                </div>

                <div className="border-t border-gray-300 pt-6 text-center text-gray-500 text-sm">
                    <p>&copy; 2026 Community Plus Care Services. All rights reserved.</p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;

